import React from 'react';
import { Facebook, Twitter, Instagram, Linkedin, Phone, MapPin, Youtube, MessageCircle, Music } from 'lucide-react';
import { useSettings } from '../../contexts/SettingsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from '../../contexts/TranslationContext';
import Image from '../AppImage';
import LanguageSwitcher from '../LanguageSwitcher';
import ThemeToggle from '../ThemeToggle';

interface FooterProps {
  className?: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
  const { platformName, platformDescription, systemLogo, systemLogoDark, showSystemName, socialMediaLinks } = useSettings();
  const { effectiveTheme } = useTheme();
  const { t, isLoading: translationsLoading } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Helper function to get translation with fallback
  const getTranslation = (key: string, fallback: string): string => {
    if (translationsLoading) {
      return fallback;
    }
    const translation = t(key);
    // If translation equals key, it means translation not found, use fallback
    return translation === key ? fallback : translation;
  };

  const footerLinks = {
    platform: [
      { label: getTranslation('footer.platform.about_us', 'About Us'), href: '/about' },
      { label: getTranslation('footer.platform.how_it_works', 'How It Works'), href: '/how-it-works' },
      { label: getTranslation('footer.platform.success_stories', 'Success Stories'), href: '/success-stories' },
      { label: getTranslation('footer.platform.partners', 'Partners'), href: '/partners' },
    ],
    resources: [
      { label: getTranslation('footer.resources.blog', 'Blog'), href: '/blog' },
      { label: getTranslation('footer.resources.events', 'Events'), href: '/events' },
      { label: getTranslation('footer.resources.opportunities', 'Opportunities'), href: '/opportunities' },
      { label: getTranslation('footer.resources.forums', 'Forums'), href: '/forums' },
      { label: getTranslation('footer.resources.polls', 'Polls'), href: '/polls' },
      { label: getTranslation('footer.resources.report_issue', 'Report Issue'), href: '/report-issue' },
    ],
  };

  // Build social links from settings, only include platforms with URLs (non-empty strings)
  const socialLinks = [];
  if (socialMediaLinks.facebook && socialMediaLinks.facebook.trim() !== '') {
    socialLinks.push({ icon: Facebook, href: socialMediaLinks.facebook.trim(), label: 'Facebook' });
  }
  if (socialMediaLinks.twitter && socialMediaLinks.twitter.trim() !== '') {
    socialLinks.push({ icon: Twitter, href: socialMediaLinks.twitter.trim(), label: 'Twitter' });
  }
  if (socialMediaLinks.instagram && socialMediaLinks.instagram.trim() !== '') {
    socialLinks.push({ icon: Instagram, href: socialMediaLinks.instagram.trim(), label: 'Instagram' });
  }
  if (socialMediaLinks.linkedin && socialMediaLinks.linkedin.trim() !== '') {
    socialLinks.push({ icon: Linkedin, href: socialMediaLinks.linkedin.trim(), label: 'LinkedIn' });
  }
  if (socialMediaLinks.youtube && socialMediaLinks.youtube.trim() !== '') {
    socialLinks.push({ icon: Youtube, href: socialMediaLinks.youtube.trim(), label: 'YouTube' });
  }
  if (socialMediaLinks.whatsapp && socialMediaLinks.whatsapp.trim() !== '') {
    socialLinks.push({ icon: MessageCircle, href: socialMediaLinks.whatsapp.trim(), label: 'WhatsApp' });
  }
  if (socialMediaLinks.tiktok && socialMediaLinks.tiktok.trim() !== '') {
    socialLinks.push({ icon: Music, href: socialMediaLinks.tiktok.trim(), label: 'TikTok' });
  }

  return (
    <footer className={`bg-card border-t border-border ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {(systemLogo || systemLogoDark) ? (
                <div className="h-10 overflow-hidden flex items-center justify-center">
                  <Image
                    src={effectiveTheme === 'dark' && systemLogoDark ? systemLogoDark : (systemLogo || systemLogoDark || '')}
                    alt={platformName}
                    className="h-full w-auto object-contain"
                    onError={(e) => {
                      // Silently handle logo load errors - logo might not be configured or file might not exist
                      // Only log in development mode
                      if (import.meta.env.DEV) {
                        console.warn('Footer logo failed to load:', effectiveTheme === 'dark' && systemLogoDark ? systemLogoDark : (systemLogo || systemLogoDark || ''));
                      }
                      // Set empty src to prevent repeated error triggers
                      (e.target as HTMLImageElement).src = '';
                    }}
                  />
                </div>
              ) : (
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-lg">
                    {platformName.charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              {showSystemName && (
                <div>
                  <h3 className="font-bold text-foreground text-lg">{platformName}</h3>
                  <p className="text-xs text-muted-foreground">{platformDescription}</p>
                </div>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              {getTranslation('footer.description', 'Connecting youth across Tanzania with opportunities, resources, and a supportive community to achieve their goals.')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-muted hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                    aria-label={social.label}
                  >
                    <IconComponent className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{getTranslation('footer.platform.title', 'Platform')}</h4>
            <ul className="space-y-2">
              {footerLinks.platform.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{getTranslation('footer.resources.title', 'Resources')}</h4>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">{getTranslation('footer.contact.title', 'Contact')}</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-primary" />
                <span>0800110802</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-primary" />
                <span>0800110804</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-4 h-4 text-primary" />
                <span>0800110806</span>
              </div>
              <div className="flex items-start gap-2 text-sm text-muted-foreground">
                <MapPin className="w-4 h-4 text-primary mt-0.5" />
                <span>Dodoma, Tanzania</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              {(() => {
                const copyrightText = getTranslation('footer.copyright', `© ${currentYear} ${platformName}. All rights reserved.`);
                return copyrightText.replace(':year', currentYear.toString()).replace(':platform', platformName);
              })()}
            </p>
            <div className="flex items-center gap-4">
              {/* Language and Theme Switchers */}
              <div className="flex items-center gap-2">
                <LanguageSwitcher variant="ghost" size="sm" />
                <ThemeToggle variant="icon-only" />
              </div>
              <div className="flex gap-6">
                <a href="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {getTranslation('footer.privacy_policy', 'Privacy Policy')}
                </a>
                <a href="/terms" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {getTranslation('footer.terms_of_service', 'Terms of Service')}
                </a>
                <a href="/cookies" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  {getTranslation('footer.cookie_policy', 'Cookie Policy')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;